import { GEOFENCE_KINDS, maskCardUid, socLevel, type Geofence, type LiveSnapshot, type Site } from "@fts/core";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Icon } from "../components/Icon";
import { LazyBoundary } from "../components/LazyBoundary";
import { LiveWatchButton } from "../components/LiveWatchButton";
import { PageHeader, Loading, EmptyState } from "../components/ui";
import type { LiveFeedStatus } from "../data";
import { useI18n, type MessageKey, type TFn } from "../i18n";
import { formatDateTime, relativeTime } from "../lib/format";
import { LIVE_FILTERS, buildLiveVehicles, countByFilter, matchesFilter, placedSites, type LiveFilter, type LiveVehicle } from "../lib/live";
import { navigate, type Route } from "../router/hash";
import { useSession, type TenantData } from "../state/session";
import { useLive } from "../state/useLive";
import { tr } from "../i18n/tr";
import { latestOpenByVehicle } from "../lib/alarms";
import { useAlarmCount } from "../state/alarms";

const MapCanvas = lazy(() => import("./MapCanvas"));

/** Device clocks may run a little ahead: never say "in 13 seconds" for something that already happened. */
function since(iso: string | null, locale: string, now: Date): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return relativeTime(Number.isFinite(t) && t > now.getTime() ? now.toISOString() : iso, locale, now.getTime());
}

const FEED_TONE: Record<LiveFeedStatus, string> = { live: "ok", connecting: "info", reconnecting: "warn" };

function stateLabel(t: TFn, lv: LiveVehicle): string {
  return t(`map.state.${lv.view.state}` as MessageKey);
}

/** Both dictionaries have the same keys (enforced by the Messages type), so checking one is enough. */
export function eventLabel(t: TFn, type: string | null): string | null {
  if (!type) return null;
  const key = `map.event.${type}`;
  return key in tr ? t(key as MessageKey) : t("map.event.other");
}

function linkLabel(t: TFn, link: string | null): string | null {
  if (link === "gsm" || link === "lte" || link === "wifi") return t(`map.value.link.${link}`);
  return null;
}

const SOC_TONE = { low: "crit", warn: "warn", ok: "ok" } as const;

/** Battery module (ICD v0.2 section 9): SoC badge, only when the device reports a reading. */
export function SocBadge({ s }: { s: LiveSnapshot | null }) {
  const { t, locale } = useI18n();
  if (!s || s.soc === null || s.soc === undefined || !Number.isFinite(s.soc)) return null;
  const value = Math.round(s.soc).toLocaleString(locale);
  const level = socLevel(s.soc)!;
  return (
    <span className={`badge tone-${SOC_TONE[level]} soc-badge`} title={t("map.socBadge", { value })}>
      <span className="soc-badge-icon" aria-hidden="true" />
      <span className="sr-only">{t("map.detail.soc")} </span>
      {t("modules.value.percent", { value })}
    </span>
  );
}

function FeedBadge({ status }: { status: LiveFeedStatus }) {
  const { t } = useI18n();
  return (
    <span className={`badge tone-${FEED_TONE[status]} live-feed`} role="status">
      <span className={`live-feed-dot${status === "live" ? " is-live" : ""}`} aria-hidden="true" />
      {t(`map.feed.${status}` as MessageKey)}
    </span>
  );
}

/** Operator name for the card currently on the vehicle (Sprint 5); null when no card or not known. */
function operatorOf(lv: LiveVehicle, names: Map<string, string>): string | null {
  return lv.snapshot?.operatorUid && lv.device ? (names.get(lv.device.deviceId) ?? null) : null;
}

function VehicleRow({ lv, selected, onSelect, now, operator }: { lv: LiveVehicle; selected: boolean; onSelect: () => void; now: Date; operator: string | null }) {
  const { t, locale } = useI18n();
  const s = lv.snapshot;
  const sub = operator ?? [lv.vehicle.make, lv.vehicle.model].filter(Boolean).join(" ");
  return (
    <li>
      <button type="button" className={`live-row${selected ? " is-selected" : ""}`} aria-pressed={selected} onClick={onSelect}>
        <span className={`live-dot live-${lv.view.state}${lv.view.alarm ? " is-alarm" : ""}`} aria-hidden="true" />
        <span className="live-row-main">
          <span className="live-row-title">
            <span className="mono">{lv.vehicle.fleetNo}</span>
            {lv.view.alarm && <span className="badge tone-crit">{t("map.state.alarm")}</span>}
          </span>
          <span className="live-row-sub">{sub || t("common.empty")}</span>
        </span>
        <span className="live-row-meta">
          <span className="live-row-state">{s ? stateLabel(t, lv) : t("map.noData")}</span>
          <span className="live-row-time">
            {!s ? "" : !lv.view.hasPosition ? t("map.noPosition") : lv.view.positionStale ? t("map.stale") : since(s.receivedAt ?? s.ts, locale, now)}
          </span>
        </span>
      </button>
    </li>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="live-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function DetailCard({
  lv,
  onClose,
  now,
  operator,
  openAlarmId,
  allSites,
}: {
  lv: LiveVehicle;
  /** Every site the user sees (for the live watch right on the vehicle's site). */
  allSites: Site[];
  onClose: () => void;
  now: Date;
  operator: string | null;
  /** Newest open alarm of the vehicle, if any (ADR 0011: the card links to it). */
  openAlarmId?: string | null;
}) {
  const { t, locale } = useI18n();
  const s = lv.snapshot;
  const dash = t("common.empty");
  const yesNo = (v: boolean | null, on: MessageKey, off: MessageKey) => (v === null ? dash : t(v ? on : off));
  const n = (v: number | null, digits = 0) => (v === null ? null : v.toLocaleString(locale, { maximumFractionDigits: digits }));
  return (
    <section className="live-detail" aria-label={lv.vehicle.fleetNo}>
      <header className="live-detail-head">
        <div>
          <h2 className="live-detail-title mono">{lv.vehicle.fleetNo}</h2>
          <p className="live-detail-sub">{[lv.vehicle.make, lv.vehicle.model].filter(Boolean).join(" ") || dash}</p>
        </div>
        <button type="button" className="icon-button" onClick={onClose} title={t("map.detail.close")}>
          <Icon name="close" size={18} label={t("map.detail.close")} />
        </button>
      </header>
      <div className="live-detail-state">
        <span className={`live-dot live-${lv.view.state}${lv.view.alarm ? " is-alarm" : ""}`} aria-hidden="true" />
        <strong>{s ? stateLabel(t, lv) : t("map.noData")}</strong>
        {lv.view.alarm && <span className="badge tone-crit">{t("map.state.alarm")}</span>}
        {lv.view.positionStale && <span className="badge tone-info">{t("map.stale")}</span>}
        <SocBadge s={s} />
      </div>
      {s && (
        <dl className="live-facts">
          <Fact label={t("map.detail.ignition")}>{yesNo(s.ignition, "map.value.on", "map.value.off")}</Fact>
          <Fact label={t("map.detail.speed")}>{s.speedKmh === null ? dash : t("map.value.kmh", { value: n(s.speedKmh, 1)! })}</Fact>
          <Fact label={t("map.detail.seat")}>{yesNo(s.seat, "map.value.occupied", "map.value.empty")}</Fact>
          <Fact label={t("map.detail.belt")}>{yesNo(s.belt, "map.value.fastened", "map.value.unfastened")}</Fact>
          <Fact label={t("map.detail.operator")}>
            {operator ? (
              <span title={maskCardUid(s.operatorUid) ?? undefined}>{operator}</span>
            ) : s.operatorUid ? (
              <span>
                <span className="mono">{maskCardUid(s.operatorUid)}</span> <span className="muted">· {t("map.value.unknownCard")}</span>
              </span>
            ) : (
              t("map.value.noCard")
            )}
          </Fact>
          <Fact label={t("map.detail.lastData")}>
            <span title={formatDateTime(s.receivedAt ?? s.ts, locale) ?? undefined}>{since(s.receivedAt ?? s.ts, locale, now) ?? dash}</span>
          </Fact>
          <Fact label={t("map.detail.positionAt")}>
            {!lv.view.hasPosition ? (
              t("map.noPosition")
            ) : (
              <span title={formatDateTime(s.posAt, locale) ?? undefined}>
                {since(s.posAt, locale, now) ?? dash}
                {s.posAcc !== null && <span className="muted"> · {t("map.value.accuracy", { value: n(s.posAcc)! })}</span>}
              </span>
            )}
          </Fact>
          <Fact label={t("map.detail.lastEvent")}>
            {s.lastEventType ? (
              <span title={formatDateTime(s.lastEventAt, locale) ?? undefined}>
                {eventLabel(t, s.lastEventType)} · {since(s.lastEventAt, locale, now)}
              </span>
            ) : (
              dash
            )}
          </Fact>
          <Fact label={t("map.detail.supply")}>{s.supplyV === null ? dash : t("map.value.volts", { value: n(s.supplyV, 1)! })}</Fact>
          <Fact label={t("map.detail.link")}>
            {[linkLabel(t, s.link), s.rssi !== null ? t("map.value.rssi", { value: s.rssi }) : null].filter(Boolean).join(" · ") || dash}
          </Fact>
          <Fact label={t("map.detail.device")}>
            <span className="mono">{lv.vehicle.deviceId}</span>
          </Fact>
        </dl>
      )}
      <footer className="live-detail-foot">
        {openAlarmId && (
          <a className="button button-primary" href={`#/alarms/${encodeURIComponent(openAlarmId)}`}>
            <Icon name="bell" size={16} />
            {t("map.detail.openAlarm")}
          </a>
        )}
        <LiveWatchButton vehicleId={lv.vehicle.id} siteId={lv.vehicle.siteId} sites={allSites} hasDevice={!!lv.vehicle.deviceId} />
        <a className="button button-secondary" href={`#/vehicles/${encodeURIComponent(lv.vehicle.id)}`}>
          {t("map.detail.openVehicle")}
        </a>
        <p className="live-detail-note">{t("map.privacyNote")}</p>
      </footer>
    </section>
  );
}

/** How often the live map reloads the zone overlay (ms). */
const ZONES_REFRESH_MS = 60_000;

export function LiveMap({ data, route }: { data: TenantData; route: Route }) {

  const { t } = useI18n();
  const { provider, tenant } = useSession();
  const live = useLive(provider, tenant!.id);
  const [filter, setFilter] = useState<LiveFilter>("all");
  const [q, setQ] = useState("");
  const [fitToken, setFitToken] = useState(0);
  const [mapError, setMapError] = useState(false);
  const selectedId = route.query.get("v");
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const tenantId = tenant?.id;
  // Re-read names when a card on any vehicle changes (Realtime brings the new card, not the name).
  const cardKey = useMemo(() => [...live.snapshots.values()].map((s) => `${s.deviceId}:${s.operatorUid ?? ""}`).sort().join("|"), [live.snapshots]);
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    provider
      .listLiveOperators(tenantId)
      .then((rows) => !cancelled && setNames(new Map(rows.map((r) => [r.deviceId, r.fullName]))))
      .catch(() => undefined); // names are a convenience; the map works without them
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, data.operators, cardKey]);

  // Open alarms per vehicle, re-read when a new alarm reaches the live feed or the badge count changes.
  const { version: alarmVersion } = useAlarmCount();
  const [openAlarms, setOpenAlarms] = useState<Map<string, string>>(new Map());
  const alarmKey = useMemo(() => [...live.snapshots.values()].map((s) => `${s.deviceId}:${s.lastAlarmAt ?? ""}`).sort().join("|"), [live.snapshots]);
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    provider
      .listAlarms(tenantId, { status: "open", limit: 200 })
      .then((list) => !cancelled && setOpenAlarms(new Map([...latestOpenByVehicle(list)].map(([v, a]) => [v, a.id]))))
      .catch(() => undefined); // the link is a convenience
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, alarmKey, alarmVersion]);

  // Zones (ADR 0015): active ones are drawn as a layer (also zones of parent sites, which apply to a scoped
  // user's vehicles); the overlay can be switched off. Refetched every minute and when the tab regains
  // focus, so zones edited elsewhere show up without a reload.
  const [zones, setZones] = useState<Geofence[]>([]);
  const [showZones, setShowZones] = useState(true);
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    const load = () =>
      provider
        .listGeofences(tenantId)
        .then((list) => !cancelled && setZones(list.filter((z) => z.active)))
        .catch(() => undefined); // the overlay is a convenience
    void load();
    const timer = setInterval(() => void load(), ZONES_REFRESH_MS);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [provider, tenantId]);

  const vehicles = useMemo(
    () => buildLiveVehicles(data.vehicles, live.devices ?? data.devices, live.snapshots, live.now),
    [data.vehicles, data.devices, live.devices, live.snapshots, live.now],
  );
  const counts = useMemo(() => countByFilter(vehicles), [vehicles]);
  const shown = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    return vehicles.filter(
      (lv) =>
        matchesFilter(lv, filter) &&
        (!needle ||
          [lv.vehicle.fleetNo, lv.vehicle.make, lv.vehicle.model, operatorOf(lv, names)].filter(Boolean).join(" ").toLocaleLowerCase("tr").includes(needle)),
    );
  }, [vehicles, filter, q, names]);
  const sites = useMemo(() => placedSites(data.sites), [data.sites]);
  const selected = vehicles.find((lv) => lv.vehicle.id === selectedId) ?? null;

  // Phones: the list is a bottom sheet over a full-height map (ADR 0030); it folds away when a vehicle opens.
  const [sheetOpen, setSheetOpen] = useState(false);
  const select = (id: string | null) => {
    if (id) setSheetOpen(false);
    navigate("/map", id ? { v: id } : undefined, { replace: true });
  };

  const header = (
    <PageHeader
      title={t("map.title")}
      subtitle={t("map.subtitle", { tenant: tenant?.name ?? "" })}
      actions={
        vehicles.length > 0 && (
          <>
            <FeedBadge status={live.status} />
            <button type="button" className="button button-secondary" onClick={() => setFitToken((x) => x + 1)}>
              {t("map.fitAll")}
            </button>
          </>
        )
      }
    />
  );

  if (live.loaded && vehicles.length === 0)
    return (
      <>
        {header}
        <EmptyState icon="map" title={t("map.empty")}>
          {t("map.emptyBody")}
        </EmptyState>
      </>
    );

  return (
    <>
      {header}
      {live.error && (
        <p className="form-error" role="alert">
          {t("map.loadError")}
        </p>
      )}
      <div className={`live-layout${selected ? " has-detail" : ""}`}>
        <section className={`live-list${sheetOpen ? " is-open" : ""}`} aria-label={t("map.listLabel")}>
          <button type="button" className="live-sheet-toggle" aria-expanded={sheetOpen} aria-controls="live-list-body" onClick={() => setSheetOpen((x) => !x)}>
            <span className="live-sheet-handle" aria-hidden="true" />
            <span>{t("map.sheet.toggle", { count: shown.length })}</span>
            {counts.alarm > 0 && <span className="badge tone-crit">{t("map.sheet.alarms", { count: counts.alarm })}</span>}
            <Icon name="chevronDown" size={16} className="live-sheet-chevron" />
          </button>
          <div id="live-list-body" className="live-list-body">
          <div className="live-list-tools">
            <input
              type="search"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("map.search")}
              aria-label={t("map.search")}
            />
            <div className="live-filters" role="group" aria-label={t("map.filterLabel")}>
              {LIVE_FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`chip${filter === f ? " is-active" : ""}`}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f !== "all" && <span className={`live-dot live-${f === "alarm" ? "alarm" : f}`} aria-hidden="true" />}
                  {t(f === "all" ? "map.filter.all" : (`map.state.${f}` as MessageKey))}
                  <span className="chip-count">{counts[f]}</span>
                </button>
              ))}
            </div>
          </div>
          {!live.loaded ? (
            <Loading />
          ) : shown.length === 0 ? (
            <p className="live-list-empty">{t("map.noMatch")}</p>
          ) : (
            <ul className="live-rows">
              {shown.map((lv) => (
                <VehicleRow key={lv.vehicle.id} lv={lv} now={live.now} operator={operatorOf(lv, names)} selected={lv.vehicle.id === selectedId} onSelect={() => select(lv.vehicle.id)} />
              ))}
            </ul>
          )}
          </div>
        </section>
        <div className="live-map">
          {mapError ? (
            <EmptyState icon="map" title={t("map.mapError")} />
          ) : (
            <LazyBoundary>
            <Suspense
              fallback={
                <Loading shape="block" />
              }
            >
              <MapCanvas
                sites={sites}
                vehicles={shown}
                allVehicles={vehicles}
                ready={live.loaded}
                selectedId={selectedId}
                onSelect={(id) => select(id)}
                fitToken={fitToken}
                label={t("map.mapLabel")}
                vehicleLabel={(lv) => t("map.vehiclePin", { fleetNo: lv.vehicle.fleetNo, state: stateLabel(t, lv) })}
                siteLabel={(s: Site) => t("map.sitePin", { name: s.name })}
                onError={() => setMapError(true)}
                geofences={zones}
                showGeofences={showZones}
              />
            </Suspense>
            </LazyBoundary>
          )}
          {zones.length > 0 && !mapError && (
            <div className="live-zones">
              <label className="check-row">
                <input type="checkbox" checked={showZones} onChange={(e) => setShowZones(e.target.checked)} />
                <span>{t("map.zones.toggle")}</span>
              </label>
              {showZones && (
                <ul className="live-zones-legend" aria-label={t("map.zones.legend")}>
                  {GEOFENCE_KINDS.filter((k) => zones.some((z) => z.kind === k)).map((k) => (
                    <li key={k}>
                      <span className={`zone-swatch zone-${k}`} aria-hidden="true" />
                      {t(`geofence.kind.${k}` as MessageKey)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <ul className="live-legend" aria-hidden="true">
            {(["working", "idle", "off", "offline", "alarm"] as const).map((k) => (
              <li key={k}>
                <span className={`live-dot live-${k}`} />
                {t(`map.state.${k}` as MessageKey)}
              </li>
            ))}
            <li>
              <span className="live-dot live-working is-stale" />
              {t("map.stale")}
            </li>
          </ul>
          {selected && (
            <DetailCard
              lv={selected}
              now={live.now}
              operator={operatorOf(selected, names)}
              openAlarmId={openAlarms.get(selected.vehicle.id) ?? null}
              allSites={data.sites}
              onClose={() => select(null)}
            />
          )}
        </div>
      </div>
    </>
  );
}
